import type { StudioRoute } from '@/store/types'
import type { StudioDictionary } from '@/i18n/studio-dictionaries'
import { APPLIANCE_HIDDEN_ROUTES } from '@/services/appliance-commerce-surface'
import { isApplianceDistribution } from '@/services/studio-presentation-profile'

export type StudioRouteUxStatus = 'renewed' | 'needs-audit' | 'legacy-alias'
export type StudioNavigationSection = 'start' | 'build' | 'verify' | 'release'
export type StudioRouteVisibility = 'global' | 'contextual' | 'privileged'

/**
 * Who hosts the conversation on this screen.
 *
 * `'route'`   — the screen brings its own request box (Home's hero composer,
 *               Bot Mode's canvas input). The shell adds nothing.
 * `'shell-rail'` — the screen has no composer of its own, so the shell's chat
 *               rail carries the conversation.
 *
 * REQUIRED, WITH NO DEFAULT, on every entry. That is the whole mechanism: a
 * route added tomorrow does not compile until somebody answers the question,
 * and "the rail, because that is what happens when you say nothing" stops
 * being an answer anyone can give by accident. The rule is deliberately NOT
 * an ordinal over the nav order ("from 작업 onward") — an ordinal is a fact
 * about today's sidebar, and it silently re-decides for every screen whenever
 * that sidebar is re-ordered.
 */
export type StudioRouteConversationOwner = 'route' | 'shell-rail'

const GLOBAL_ROUTE_SECTIONS = {
  home: 'start',
  // Bot Mode is the product's front door -- a bot's reply IS the working UI --
  // so it sits in `start`, next to home. It shipped under `verify` beside
  // "Requests & Reviews", which read as a place you go to CHECK things after
  // the fact; the IA said the opposite of the plan.
  //
  // Position here is load-bearing twice: this map is the route -> section
  // lookup AND, through `Object.keys`, the nav rail's display order.
  bots: 'start',
  projects: 'start',
  import: 'build',
  'design-system': 'build',
  library: 'build',
  agent: 'verify',
  'apps-live': 'release',
  commerce: 'release',
} as const

const CONTEXTUAL_ROUTES = [
  'new-project', 'templates', 'documents', 'map', 'design-system-edit', 'settings',
  'products', 'orders', 'revenue', 'members', 'billing', 'analytics',
] as const

const PRIVILEGED_ROUTES = ['admin'] as const

/**
 * Where a route's display name comes from in the locale dictionary.
 *
 * `navigation.<key>` for the flat keys, `sections.<key>` for the four IA
 * section names (`Start` / `Build` / `Verify` / `Release`), and
 * `routeHeadings.<key>` for the four screens whose name already lived in the
 * `routeHeadings` table (their page H1s were localized; the registry was not,
 * so every OTHER surface that names those screens — breadcrumb, nav rail,
 * command palette, and the truncation `title` a breadcrumb renders — fell
 * back to the English `label` in every locale).
 *
 * `labelKey` is optional in the type because a registry entry is legal
 * without one; that it is now present on EVERY entry is asserted by the
 * accessibility gate, not by the type.
 */
export type StudioRouteLabelKey =
  | keyof Omit<StudioDictionary['navigation'], 'sections'>
  | `sections.${keyof StudioDictionary['navigation']['sections']}`
  | `routeHeadings.${keyof StudioDictionary['routeHeadings']}`

export interface StudioRouteRegistryEntry {
  subRoute: string
  route: StudioRoute
  canonicalPath: string
  /**
   * The user-facing name of the screen, in English.
   *
   * SINGLE SOURCE OF TRUTH — the nav rail, the breadcrumb, the command
   * palette, and the UX gates all resolve the screen name from here (via
   * `studioRouteLabel`, which prefers `labelKey` so the name stays
   * translatable). Before this was centralised the same name existed as four
   * independent copies and drifted: this field said `Projects` / `Automation`
   * while every rendered surface said `Work` / `Requests`.
   *
   * `label` and `labelKey` are locked in lockstep by
   * `__tests__/studio-route-map.test.ts` — editing one without the other
   * fails the suite.
   */
  label: string
  labelKey?: StudioRouteLabelKey
  /** Exact level-one heading used by the route audit. */
  pageHeading: string
  navigationSection?: StudioNavigationSection
  navigationVisibility: StudioRouteVisibility
  /**
   * Breadcrumb parent. `home` for top-level screens; the Operations sub-area
   * screens point at `commerce`, which yields `Home / Operations / <Leaf>`.
   * Omitted on `home` itself (the root has no parent).
   */
  parent?: StudioRoute
  island: string | null
  primaryAction: string
  emptyState: string
  /**
   * Whether this screen hosts its own conversation or leans on the shell rail.
   *
   * Note the absence of a `?`. See {@link StudioRouteConversationOwner}: the
   * required-ness IS the enforcement, and `studioRouteOwnsConversation` below
   * is the single derivation every surface reads.
   */
  conversationOwner: StudioRouteConversationOwner
  uxStatus: StudioRouteUxStatus
  legacyAliasFor?: string
}

/**
 * Every registered route, distribution-neutral.
 *
 * NOT exported — {@link STUDIO_ROUTE_REGISTRY} below is the one name every
 * consumer in this codebase imports, and it is distribution-filtered. This
 * array exists only so that filter has a single, named source to read.
 */
const STUDIO_ROUTE_REGISTRY_ALL = [
  {
    subRoute: '',
    route: 'home',
    canonicalPath: '/studio',
    conversationOwner: 'route',
    label: 'Home',
    labelKey: 'home',
    pageHeading: 'What would you like to make?',
    navigationSection: GLOBAL_ROUTE_SECTIONS.home,
    navigationVisibility: 'global',
    island: 'studio.dashboard.content.home',
    primaryAction: 'Create or import an app',
    emptyState: 'Show Studio entry cards and recent projects',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'new',
    route: 'new-project',
    canonicalPath: '/studio/new',
    conversationOwner: 'shell-rail',
    label: 'Create',
    labelKey: 'create',
    pageHeading: 'What are we making?',
    navigationVisibility: 'contextual',
    parent: 'home',
    island: 'studio.dashboard.content.new-project',
    primaryAction: 'Start a new app, document, or report',
    emptyState: 'Guide the user through request, source, review, and share',
    uxStatus: 'renewed',
  },
  {
    // `subRoute` is the permalink, `label` is the product name. They diverge
    // on purpose here (and on `agent` / `commerce` / `members`): the IA v3
    // rename moved the screen name Projects → Work while the URL stayed put
    // so shipped links keep resolving. Renaming the path is the one fix that
    // is NOT available.
    subRoute: 'projects',
    route: 'projects',
    canonicalPath: '/studio/projects',
    conversationOwner: 'shell-rail',
    label: 'Work',
    labelKey: 'work',
    pageHeading: 'Work',
    navigationSection: GLOBAL_ROUTE_SECTIONS.projects,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.projects',
    primaryAction: 'Open, create, or publish an app',
    emptyState: 'Create a first app or import from an external source',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'apps-live',
    route: 'apps-live',
    canonicalPath: '/studio/apps-live',
    conversationOwner: 'shell-rail',
    label: 'Share',
    labelKey: 'share',
    pageHeading: 'Share',
    navigationSection: GLOBAL_ROUTE_SECTIONS['apps-live'],
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.apps-live',
    primaryAction: 'Operate deployed Cloudflare apps',
    emptyState: 'Create or import an app and publish it',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'documents',
    route: 'documents',
    canonicalPath: '/studio/documents',
    conversationOwner: 'shell-rail',
    label: 'Documents',
    labelKey: 'documents',
    pageHeading: 'Document',
    navigationVisibility: 'contextual',
    parent: 'home',
    island: 'studio.dashboard.content.documents',
    primaryAction: 'Draft a document and export it to DOCX or HWPX',
    emptyState: 'Add a page to start the document',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'templates',
    route: 'templates',
    canonicalPath: '/studio/templates',
    conversationOwner: 'shell-rail',
    label: 'Starters',
    labelKey: 'starters',
    pageHeading: 'Starters',
    navigationVisibility: 'contextual',
    parent: 'home',
    island: 'studio.dashboard.content.templates',
    primaryAction: 'Start from a reusable workspace',
    emptyState: 'Clear filters or describe the artifact to create',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'import',
    route: 'import',
    canonicalPath: '/studio/import',
    conversationOwner: 'shell-rail',
    label: 'Sources',
    labelKey: 'sources',
    pageHeading: 'Sources',
    navigationSection: GLOBAL_ROUTE_SECTIONS.import,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.import',
    primaryAction: 'Turn a prompt, file, URL, repo, or design source into a living artifact',
    emptyState: 'Choose a source and create a preview',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'library',
    route: 'library',
    canonicalPath: '/studio/library',
    conversationOwner: 'shell-rail',
    label: 'Library',
    labelKey: 'library',
    pageHeading: 'Library',
    navigationSection: GLOBAL_ROUTE_SECTIONS.library,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.library',
    primaryAction: 'Review apps, documents, reports, share links, readiness records, and reusable outputs',
    emptyState: 'Import or create an app to add the first library item',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'map',
    route: 'map',
    canonicalPath: '/studio/map',
    conversationOwner: 'shell-rail',
    label: 'App Map',
    labelKey: 'routeHeadings.map',
    pageHeading: 'App Map',
    navigationVisibility: 'contextual',
    parent: 'projects',
    island: 'studio.dashboard.content.map',
    primaryAction: 'Inspect app structure',
    emptyState: 'Open a project to map pages, data, and actions',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'agent',
    route: 'agent',
    canonicalPath: '/studio/agent',
    conversationOwner: 'shell-rail',
    label: 'Requests & Reviews',
    labelKey: 'requestsAndReviews',
    pageHeading: 'Requests & Reviews',
    navigationSection: GLOBAL_ROUTE_SECTIONS.agent,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.agent',
    primaryAction: 'Review durable request history; approve pending actions inline in Chat or Bot',
    emptyState: 'Run an automation or import flow; this route appears in navigation when an approval needs attention',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'bots',
    route: 'bots',
    canonicalPath: '/studio/bots',
    conversationOwner: 'route',
    label: 'Bots',
    labelKey: 'bots',
    pageHeading: 'Bots',
    navigationSection: GLOBAL_ROUTE_SECTIONS.bots,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.bots',
    primaryAction: 'Ask a bot to build or review something',
    emptyState: 'Pick a bot and describe what you need',
    uxStatus: 'renewed',
  },
  {
    // Two screens were both called `Design System`, so the breadcrumb read
    // `Home / Design System` on either and neither name told you which one you
    // were on. This one is the Design System: it lists what the workspace has
    // and holds the theme editor. `design-system-edit` below is the same
    // editor at its own address, and is named for what it is.
    subRoute: 'design-system',
    route: 'design-system',
    canonicalPath: '/studio/design-system',
    conversationOwner: 'shell-rail',
    label: 'Design System',
    labelKey: 'designSystem',
    pageHeading: 'Design System',
    navigationSection: GLOBAL_ROUTE_SECTIONS['design-system'],
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.design-system',
    primaryAction: 'Set the colours, type, and shape that generated apps use',
    emptyState: 'Save a first design system from the theme editor',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'design-system-edit',
    route: 'design-system-edit',
    canonicalPath: '/studio/design-system-edit',
    conversationOwner: 'shell-rail',
    label: 'Theme editor',
    labelKey: 'routeHeadings.themeEditor',
    pageHeading: 'Theme editor',
    navigationVisibility: 'contextual',
    parent: 'design-system',
    island: 'studio.dashboard.content.design-system-edit',
    primaryAction: 'Edit design tokens and preview components',
    emptyState: 'Choose a preset or start from the cogo base theme',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'settings',
    route: 'settings',
    canonicalPath: '/studio/settings',
    conversationOwner: 'shell-rail',
    label: 'Workspace Settings',
    labelKey: 'workspaceSettings',
    pageHeading: 'Workspace Settings',
    navigationVisibility: 'contextual',
    parent: 'home',
    island: 'studio.dashboard.content.settings',
    primaryAction: 'Manage account, billing, domain, and integrations',
    emptyState: 'Connect account settings',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'admin',
    route: 'admin',
    canonicalPath: '/studio/admin',
    conversationOwner: 'shell-rail',
    label: 'Admin',
    labelKey: 'routeHeadings.admin',
    pageHeading: 'Admin',
    navigationVisibility: 'privileged',
    parent: 'home',
    island: 'studio.dashboard.content.admin',
    primaryAction: 'Review privileged admin state',
    emptyState: 'Show access requirements',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'commerce',
    route: 'commerce',
    canonicalPath: '/studio/commerce',
    conversationOwner: 'shell-rail',
    label: 'Operations',
    labelKey: 'operations',
    pageHeading: 'Operations',
    navigationSection: GLOBAL_ROUTE_SECTIONS.commerce,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.commerce',
    primaryAction: 'Review shared artifact activity',
    emptyState: 'Share an app, document, or report',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'products',
    route: 'products',
    canonicalPath: '/studio/products',
    conversationOwner: 'shell-rail',
    label: 'Data',
    labelKey: 'data',
    pageHeading: 'Data',
    navigationVisibility: 'contextual',
    parent: 'commerce',
    island: 'studio.dashboard.content.products',
    primaryAction: 'Review source data',
    emptyState: 'Add the first source packet',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'orders',
    route: 'orders',
    canonicalPath: '/studio/orders',
    conversationOwner: 'shell-rail',
    label: 'Activity',
    labelKey: 'activity',
    pageHeading: 'Activity',
    navigationVisibility: 'contextual',
    parent: 'commerce',
    island: 'studio.dashboard.content.orders',
    primaryAction: 'Review artifact activity',
    emptyState: 'Activity appears after sharing',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'revenue',
    route: 'revenue',
    canonicalPath: '/studio/revenue',
    conversationOwner: 'shell-rail',
    label: 'Impact',
    labelKey: 'impact',
    pageHeading: 'Impact',
    navigationVisibility: 'contextual',
    parent: 'commerce',
    island: 'studio.dashboard.content.revenue',
    primaryAction: 'Review usage and outcomes',
    emptyState: 'Impact appears after sharing',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'members',
    route: 'members',
    canonicalPath: '/studio/members',
    conversationOwner: 'shell-rail',
    label: 'Audience',
    labelKey: 'audience',
    pageHeading: 'Audience',
    navigationVisibility: 'contextual',
    parent: 'commerce',
    island: 'studio.dashboard.content.audience',
    primaryAction: 'Review members and traffic sources',
    emptyState: 'Audience appears after publishing',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'billing',
    route: 'billing',
    canonicalPath: '/studio/billing',
    conversationOwner: 'shell-rail',
    label: 'Billing',
    labelKey: 'routeHeadings.billing',
    pageHeading: 'Billing',
    navigationVisibility: 'contextual',
    parent: 'settings',
    island: 'studio.dashboard.content.billing',
    primaryAction: 'Review subscription and invoices',
    emptyState: 'Choose a plan',
    uxStatus: 'renewed',
  },
  {
    subRoute: 'analytics',
    route: 'analytics',
    canonicalPath: '/studio/analytics',
    conversationOwner: 'shell-rail',
    label: 'Analytics',
    labelKey: 'analytics',
    pageHeading: 'Analytics',
    navigationVisibility: 'contextual',
    parent: 'commerce',
    island: 'studio.dashboard.content.analytics',
    primaryAction: 'Review deployed app analytics',
    emptyState: 'Open a live app to view analytics',
    uxStatus: 'renewed',
  },
  // ── Aliases ───────────────────────────────────────────────────────────
  //
  // An alias earns a row here when the token is already a durable identifier
  // somewhere else in the system — a URL that shipped (`apps`, `explore`,
  // `cogo-xyz`) or the island/page name a screen is built under
  // (`audience` → `studio.dashboard.content.audience`). Display names alone
  // (`work`, `review`, `data`, `impact`) do NOT qualify: nothing has ever
  // linked to them, and minting a URL per product-copy revision is what
  // produced the naming drift these rows exist to absorb.
  {
    subRoute: 'apps',
    route: 'projects',
    canonicalPath: '/studio/projects',
    conversationOwner: 'shell-rail',
    label: 'Work',
    labelKey: 'work',
    pageHeading: 'Work',
    navigationSection: GLOBAL_ROUTE_SECTIONS.projects,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.projects',
    primaryAction: 'Legacy alias to Work',
    emptyState: 'Redirect to Work',
    uxStatus: 'legacy-alias',
    legacyAliasFor: 'projects',
  },
  {
    subRoute: 'explore',
    route: 'templates',
    canonicalPath: '/studio/templates',
    conversationOwner: 'shell-rail',
    label: 'Starters',
    labelKey: 'starters',
    pageHeading: 'Starters',
    navigationVisibility: 'contextual',
    parent: 'home',
    island: 'studio.dashboard.content.templates',
    primaryAction: 'Legacy alias to Starters',
    emptyState: 'Redirect to Starters',
    uxStatus: 'legacy-alias',
    legacyAliasFor: 'templates',
  },
  {
    subRoute: 'cogo-xyz',
    route: 'library',
    canonicalPath: '/studio/library',
    conversationOwner: 'shell-rail',
    label: 'Library',
    labelKey: 'library',
    pageHeading: 'Library',
    navigationSection: GLOBAL_ROUTE_SECTIONS.library,
    navigationVisibility: 'global',
    parent: 'home',
    island: 'studio.dashboard.content.library',
    primaryAction: 'Legacy alias to Library',
    emptyState: 'Redirect to Library',
    uxStatus: 'legacy-alias',
    legacyAliasFor: 'library',
  },
  {
    // `audience` is the name this screen carries everywhere except its URL:
    // the island is `studio.dashboard.content.audience`, the page component
    // is `AudiencePage`, and the label is `Audience`. `/studio/audience` was
    // the one spelling a person would guess and the only one that resolved
    // to the home chat screen instead.
    subRoute: 'audience',
    route: 'members',
    canonicalPath: '/studio/members',
    conversationOwner: 'shell-rail',
    label: 'Audience',
    labelKey: 'audience',
    pageHeading: 'Audience',
    navigationVisibility: 'contextual',
    parent: 'commerce',
    island: 'studio.dashboard.content.audience',
    primaryAction: 'Alias to Audience',
    emptyState: 'Redirect to Audience',
    uxStatus: 'legacy-alias',
    legacyAliasFor: 'members',
  },
] as const satisfies readonly StudioRouteRegistryEntry[]

/**
 * The commerce/"Operations" route exclusion now lives in ONE place shared by
 * every appliance surface — `@/services/appliance-commerce-surface`. It used
 * to be a set declared right here, and that locality is exactly how `admin`
 * survived the first removal pass and how three further template inventories
 * (`FEATURED_TEMPLATES`, `TEMPLATE_REGISTRY`, the public `/pricing` +
 * `/checkout/*` pages) were never covered at all: one list per surface means
 * one list to forget. The rationale for each hidden route is documented at
 * the declaration site.
 */

/**
 * Every registered route, EXCEPT — under an appliance distribution — the
 * commerce surface above.
 *
 * THE single exclusion point. Every consumer that walks routes for nav
 * (`STUDIO_GLOBAL_NAV_SECTIONS`, the rail + command palette), resolution
 * (`STUDIO_ROUTE_MAP`, direct-URL + `isKnownStudioPathname`), or breadcrumb
 * (`studioRouteAncestry`) reads THIS export — not `STUDIO_ROUTE_REGISTRY_ALL`
 * — so a distribution change here propagates to all of them without a
 * per-consumer conditional. Evaluated once at module load: `VITE_*` values
 * are a Vite build-time constant, not a value that changes within a running
 * build, so a one-time filter here is equivalent to (and cheaper than) each
 * consumer re-checking the predicate on every read.
 *
 * Under the default (cloud) distribution this is a no-op — identical to
 * `STUDIO_ROUTE_REGISTRY_ALL` — so every existing consumer (heading-pin
 * tests, the route-map coverage test, label lockstep) is unaffected.
 */
export const STUDIO_ROUTE_REGISTRY: readonly StudioRouteRegistryEntry[] = isApplianceDistribution()
  ? STUDIO_ROUTE_REGISTRY_ALL.filter((entry) => !APPLIANCE_HIDDEN_ROUTES.has(entry.route))
  : STUDIO_ROUTE_REGISTRY_ALL

export const STUDIO_ROUTE_MAP: Record<string, StudioRoute> = Object.fromEntries(
  STUDIO_ROUTE_REGISTRY.map((entry) => [entry.subRoute, entry.route]),
) as Record<string, StudioRoute>

export const STUDIO_GLOBAL_NAV_SECTIONS: ReadonlyArray<{
  id: StudioNavigationSection
  routes: StudioRoute[]
}> = (['start', 'build', 'verify', 'release'] as const).map((id) => ({
  id,
  routes: STUDIO_ROUTE_REGISTRY
    .filter(
      (entry) =>
        !('legacyAliasFor' in entry) &&
        entry.navigationVisibility === 'global' &&
        entry.navigationSection === id,
    )
    .map((entry) => entry.route)
    .sort(
      (left, right) =>
        Object.keys(GLOBAL_ROUTE_SECTIONS).indexOf(left) -
        Object.keys(GLOBAL_ROUTE_SECTIONS).indexOf(right),
    ),
}))

export const STUDIO_ROUTE_CANONICAL_PATH: Partial<Record<string, string>> = Object.fromEntries(
  STUDIO_ROUTE_REGISTRY
    .filter((entry) => Object.prototype.hasOwnProperty.call(entry, 'legacyAliasFor'))
    .map((entry) => [entry.subRoute, entry.canonicalPath]),
) as Partial<Record<string, string>>

/**
 * Every registered sub-route → its canonical path, aliases included.
 *
 * `STUDIO_ROUTE_CANONICAL_PATH` above only covers aliases, so a URL that
 * spells a *registered* route non-canonically (`/studio/` for `/studio`,
 * `/studio//members`) had nothing to normalise against and kept its odd
 * address. Callers compare against the request path and rewrite only when
 * the two differ.
 */
export const STUDIO_SUB_ROUTE_CANONICAL_PATH: Partial<Record<string, string>> = Object.fromEntries(
  STUDIO_ROUTE_REGISTRY.map((entry) => [entry.subRoute, entry.canonicalPath]),
) as Partial<Record<string, string>>

export const STUDIO_ROUTE_CANONICAL_PATH_BY_ROUTE: Record<StudioRoute | 'home', string> = Object.fromEntries(
  STUDIO_ROUTE_REGISTRY.flatMap((entry) => {
    const values: Array<[string, string]> = [[entry.route, entry.canonicalPath]]
    if (Object.prototype.hasOwnProperty.call(entry, 'legacyAliasFor')) {
      values.push([entry.subRoute, entry.canonicalPath])
    }
    return values
  }),
) as Record<StudioRoute | 'home', string>

export const STUDIO_ROUTE_AUDIT_PATHS = STUDIO_ROUTE_REGISTRY.map((entry) => entry.canonicalPath)
  .filter((path, index, all) => all.indexOf(path) === index)

// ─── Display names ──────────────────────────────────────────────────────
//
// One lookup, one answer. The nav rail, the breadcrumb, the command palette,
// and the not-found page all name a screen through `studioRouteLabel`, so a
// rename lands in exactly one place. Previously each surface carried its own
// copy and they drifted apart — `/studio/projects` was `Projects` in the
// registry and `Work` on screen, `/studio/agent` `Automation` vs `Requests`.

/**
 * Route → registry entry.
 *
 * Canonical entries key on `route`; alias entries key on `subRoute`, because
 * `apps` / `explore` / `cogo-xyz` are themselves `StudioRoute` values that
 * the store can briefly hold before the shell redirects them. A few alias
 * sub-routes (`audience`) are URL-only spellings that never become a
 * `StudioRoute`, so their key here is simply never read.
 */
const ENTRY_BY_ROUTE: Record<string, StudioRouteRegistryEntry> = (() => {
  const byRoute: Record<string, StudioRouteRegistryEntry> = {}
  for (const entry of STUDIO_ROUTE_REGISTRY) {
    if (!Object.prototype.hasOwnProperty.call(entry, 'legacyAliasFor')) {
      byRoute[entry.route] = entry
    }
  }
  for (const entry of STUDIO_ROUTE_REGISTRY) {
    if (Object.prototype.hasOwnProperty.call(entry, 'legacyAliasFor') && !byRoute[entry.subRoute]) {
      byRoute[entry.subRoute] = entry
    }
  }
  return byRoute
})()

export function studioRouteEntry(route: StudioRoute | 'home'): StudioRouteRegistryEntry | undefined {
  return ENTRY_BY_ROUTE[route]
}

/**
 * Does this screen bring its own request box?
 *
 * THE one derivation. Every surface that decides whether to add a second
 * conversation reads this — never a hard-coded route list, never a position
 * in the nav order.
 *
 * An unregistered route answers `false`, i.e. the shell rail carries the
 * conversation. That is the pre-existing behaviour of every screen and the
 * safe direction to be wrong in: a route the registry does not know cannot
 * have a composer the registry does not know about either, so the failure
 * mode is "a rail where none was wanted", not "no way to say anything".
 */
export function studioRouteOwnsConversation(route: StudioRoute | 'home'): boolean {
  return ENTRY_BY_ROUTE[route]?.conversationOwner === 'route'
}

/**
 * The screen's name in the active locale.
 *
 * `labelKey` resolves against the dictionary so the name translates; routes
 * with no dictionary entry fall back to the registry's English `label`
 * (kept equal to the English dictionary value by a lockstep unit test).
 * Unknown routes return the raw route rather than an invented name — a
 * placeholder that reads as broken is preferable to one that reads as real.
 */
export function studioRouteLabel(
  route: StudioRoute | 'home',
  strings: StudioDictionary,
): string {
  const entry = ENTRY_BY_ROUTE[route]
  if (!entry) return route
  const key = entry.labelKey
  if (!key) return entry.label
  if (key.startsWith('sections.')) {
    const sectionKey = key.slice('sections.'.length) as keyof StudioDictionary['navigation']['sections']
    return strings.navigation.sections[sectionKey] ?? entry.label
  }
  if (key.startsWith('routeHeadings.')) {
    const headingKey = key.slice('routeHeadings.'.length) as keyof StudioDictionary['routeHeadings']
    return strings.routeHeadings[headingKey] ?? entry.label
  }
  return (strings.navigation[key as keyof Omit<StudioDictionary['navigation'], 'sections'>] as string) ?? entry.label
}

/**
 * The breadcrumb chain for a route, root-first, ending with the route
 * itself: `home` → `['home']`, `members` → `['home', 'commerce', 'members']`.
 *
 * Walking `parent` links keeps the chain a property of the registry instead
 * of a hand-maintained map in the header component. The visited guard stops
 * a mis-edited `parent` cycle from hanging the render.
 */
export function studioRouteAncestry(route: StudioRoute | 'home'): Array<StudioRoute | 'home'> {
  const chain: Array<StudioRoute | 'home'> = []
  const seen = new Set<string>()
  let cursor: StudioRoute | 'home' | undefined = route
  while (cursor && !seen.has(cursor)) {
    seen.add(cursor)
    chain.unshift(cursor)
    cursor = ENTRY_BY_ROUTE[cursor]?.parent
  }
  return chain
}

/**
 * Destinations offered when a Studio URL does not resolve. Kept here rather
 * than in the not-found page so the recovery list cannot name a route the
 * registry does not have.
 */
export const STUDIO_PRIMARY_DESTINATIONS = [
  'home',
  'projects',
  'apps-live',
  'templates',
  'import',
  'library',
  'agent',
] as const satisfies readonly (StudioRoute | 'home')[]
